import {useEffect, useState} from "react";
import {Box, Button, Chip, Typography} from "@mui/material";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import {useNavigate} from "react-router-dom";
import {flushSync} from "react-dom";
import {useKioskLang, useKioskSession} from "../context/KioskSessionContext";
import {KIOSK_LANGS} from "../i18n";
import StaffAccessDialog from "../components/StaffAccessDialog";
import KioskLogoutDialog from "../components/KioskLogoutDialog";
import CustomerDisplay from "../components/CustomerDisplay";
import useDoubleTap from "../hooks/useDoubleTap";
import {useGlobalOrder} from "../../utils/GlobalStateContext";
import {BRAND_ASSETS} from "../../brand";

const DISPLAY_STALE_MS = 90000;    //點餐中畫面多久沒更新自動退回
const DISPLAY_DONE_MS = 8000;      //結帳完成畫面停留秒數

export default function WelcomePage({systemData}) {
  const navigate = useNavigate();
  const {startSession, resetSession} = useKioskSession();
  const {lang, setLang, tr} = useKioskLang();
  const {kioskDisplayEvent, refreshProductData} = useGlobalOrder();
  const [staffOpen, setStaffOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [display, setDisplay] = useState(null);
  const tapLogo = useDoubleTap(() => setLogoutOpen(true));   //連點兩下 Logo 詢問是否登出

  useEffect(() => resetSession(), [resetSession]);   //回到歡迎頁即重置（語言也回到中文）

  const chooseLang = (code) => setLang(code);

  //POS 點餐事件 → 切換客顯（只在歡迎頁閒置時，本頁掛載即代表閒置）
  useEffect(() => {
    if (!kioskDisplayEvent) return;
    if (kioskDisplayEvent.status === "clear") {
      setDisplay(null);
      return;
    }
    if (Date.now() - kioskDisplayEvent.receivedAt > DISPLAY_STALE_MS) return;
    setDisplay(kioskDisplayEvent);
  }, [kioskDisplayEvent]);

  //一段時間沒有新事件自動退回歡迎頁
  useEffect(() => {
    if (!display) return;
    const timer = setTimeout(() => setDisplay(null), display.status === "done" ? DISPLAY_DONE_MS : DISPLAY_STALE_MS);
    return () => clearTimeout(timer);
  }, [display]);

  const start = () => {
    setDisplay(null);
    refreshProductData();   //閒置期間可能漏接售完推播，開始點餐前重抓（不擋畫面，回來後 systemData 會自動更新）
    flushSync(() => startSession());
    navigate("/kiosk/service");
  };

  if (display) {
    return <CustomerDisplay event={display} storeName={systemData?.store_name} onStartOrder={start}/>;
  }

  const openStaff = () => {
    setLogoutOpen(false);
    setStaffOpen(true);
  };

  return (
    <Box sx={{minHeight: "100dvh", display: "grid", gridTemplateColumns: "1.1fr .9fr", bgcolor: "#F7F1E8", overflow: "hidden"}}>
      <Box sx={{p: 6, pl: "max(48px, env(safe-area-inset-left))", display: "flex", flexDirection: "column", justifyContent: "space-between", background: "radial-gradient(circle at 20% 20%, rgba(232,163,23,.16), transparent 33%), linear-gradient(145deg,#FFF9F0,#F0DDC8)"}}>
        <Box component="img" src={systemData?.store_image || BRAND_ASSETS.icon} onClick={tapLogo} alt="店家 Logo" sx={{width: 140, height: 140, objectFit: "contain", borderRadius: "28%", boxShadow: "0 18px 50px rgba(28,45,66,.18)", userSelect: "none"}}/>
        <Box>
          <Typography sx={{fontSize: "clamp(52px,7vw,94px)", lineHeight: 1.02, letterSpacing: "-.04em", fontWeight: 950}}>{tr("welcome")}</Typography>
          <Typography sx={{fontSize: 28, mt: 2, color: "text.secondary"}}>{tr("welcomeText")}</Typography>
        </Box>
        <Box sx={{display: "flex", gap: 1.5}}>
          {systemData?.kiosk_settings?.allow_line_pay !== false && <Chip label="Line Pay" sx={{fontSize: 17, fontWeight: 800}}/>}
          {systemData?.kiosk_settings?.allow_counter_pay !== false && <Chip label={tr("counterPay")} sx={{fontSize: 17, fontWeight: 800}}/>}
        </Box>
      </Box>
      <Box sx={{p: 6, pr: "max(48px, env(safe-area-inset-right))", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#1C2D42", color: "#FFF"}}>
        <TouchAppRoundedIcon sx={{fontSize: 86, opacity: .9}}/>
        <Typography sx={{fontSize: 34, fontWeight: 900, mt: 2}}>{systemData?.store_name || tr("selfOrder")}</Typography>
        <Box sx={{mt: 4, display: "flex", gap: 1.25}}>
          {KIOSK_LANGS.map((option) => (
            <Button key={option.code} onClick={() => chooseLang(option.code)} variant={lang === option.code ? "contained" : "outlined"} sx={{minWidth: 108, minHeight: 56, fontSize: 18, fontWeight: 900, borderRadius: 99, color: lang === option.code ? "#1C2D42" : "#FFF", bgcolor: lang === option.code ? "#FFF" : "transparent", borderColor: "rgba(255,255,255,.55)", "&:hover": {bgcolor: lang === option.code ? "#FFFBEB" : "rgba(255,255,255,.12)", borderColor: "#FFF"}}}>
              {option.label}
            </Button>
          ))}
        </Box>
        <Button onClick={start} variant="contained" sx={{mt: 3.5, minWidth: 340, minHeight: 92, fontSize: 30, bgcolor: "#FFF", color: "#1C2D42", boxShadow: "0 22px 45px rgba(12,22,37,.25)", "&:hover": {bgcolor: "#FFFBEB"}}}>{tr("startOrder")}</Button>
        <Typography sx={{mt: 3, opacity: .72, fontSize: 17}}>{tr("startOrderHint")}</Typography>
      </Box>
      <KioskLogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} onStaffAccess={openStaff}/>
      <StaffAccessDialog open={staffOpen} onClose={() => setStaffOpen(false)} systemData={systemData}/>
    </Box>
  );
}
